import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { shouldGoMobile } from "../utils/mobileDetect";
import { Box, Typography, Chip, Button, CircularProgress, TextField } from "@mui/material";
import { MainPage } from "../MainPage";
import { useHighlightsAuth } from "./useHighlightsAuth";

// ─── Step definitions ─────────────────────────────────────────────────────────
// Edit `insight` for the chip that shows on each active card.
// Edit `appState` comments to describe what the dashboard should show.

// ─── EDIT THIS to change what the map shows on each step ─────────────────────
// mapView fields:
//   variable        — key from groupsById (e.g. "vote_intention", "trust_energy")
//   compareWaves    — true = show Q1→Q2 change overlay; false = show latest wave only
//   compareKey      — "__top__" = biggest gainers, "__top_neg__" = biggest losers
//   waveSelect      — wave number to display (4 = Q2 2026)
//   preferenceFilter — override sub-key shown (e.g. "pol:nat_issues:defence:cat")
//   highlightAreas  — array of PCON24CD codes to spotlight on the map
// flyTo fields:
//   flyTo        — { type: "fitBounds", bounds } or { center: [lng,lat], zoom }
//   flyTo2       — optional second flyTo fired after flyTo2Delay ms (for pan sequences)
//   flyTo2Delay  — ms before flyTo2 fires (default 2900)
// ─────────────────────────────────────────────────────────────────────────────
const STEPS = [
  {
    name: "intro",
    headline: "In our latest quarterly megapoll of British adults, energy companies are in the frame: the importance of the sector to the future of the economy is significantly up, at the same time as trust in energy companies is significantly down",
    // Methodology bullets rendered by NarrativeCard when step.methodology is true
    methodology: true,
    insight: "10K interviews · 632 constituencies",
    mapLabel: "Vote intention · Q2 2026",
    mapView: { variable: "vote_intention", compareWaves: false, waveSelect: 4 },
    flyTo: { type: "fitBounds", bounds: [[-7.8, 49.9], [1.6, 58.8]], duration: 0 },
  },
  {
    name: "energy-importance",
    headline: "Energy is now the most important sector in the UK",
    copy: "The clearest story from Q1 to Q2 is a dramatic reappraisal of energy companies, which jumped +4.5pp to 18.1% — now considered the most important sector in voters' eyes, overtaking Healthcare & Pharma (17.5%) from Q1.",
    sectorFalls: [
      { label: "Water & utilities", delta: "−4.3pp", from: "11.7%", to: "7.4%", note: "Voters may be distinguishing energy from broader utilities" },
      { label: "Tech & social media", delta: "−2.9pp", from: "5.9%", to: "3.0%", note: "Halved its perceived importance" },
      { label: "Farming & food production", delta: "−1.7pp", to: "14.7%", note: "" },
    ],
    mapHoverHint: true,
    insight: "Energy overtakes Healthcare & Pharma",
    mapLabel: "Sector importance · energy · Q2 2026",
    mapView: { variable: "sector_importance", compareWaves: false, waveSelect: 4 },
    flyTo: { type: "fitBounds", bounds: [[-7.8, 49.9], [1.6, 58.8]], duration: 1200 },
  },
  {
    name: "scotland-energy",
    headline: "The signal is strongest in Scotland",
    copy: "The five constituencies with the biggest quarter-on-quarter gains in energy importance are all in Scotland — concentrated around the Central Belt and the Firth of Forth.",
    scotlandConstituencies: [
      { name: "Dunfermline and Dollar", delta: "+7.1pp", now: "20.5%" },
      { name: "Inverclyde and Renfrewshire West", delta: "+7.0pp", now: "20.5%" },
      { name: "Motherwell, Wishaw and Carluke", delta: "+6.9pp", now: "20.4%" },
      { name: "West Dunbartonshire", delta: "+6.9pp", now: "20.3%" },
      { name: "Bathgate and Linlithgow", delta: "+6.9pp", now: "20.2%" },
    ],
    scotlandNote: "The Scottish concentration likely reflects the salience of North Sea energy policy and transition debates — constituencies where the energy transition is not an abstract climate argument but a question of local jobs and industrial future.",
    insight: "Top 5 gains all in Scotland",
    mapLabel: "Sector importance · energy · Scottish constituencies",
    mapView: {
      variable: "sector_importance", compareWaves: false, waveSelect: 4,
      highlightAreas: ["S14000076", "S14000093", "S14000099", "S14000109", "S14000063"],
    },
    flyTo: { type: "fitBounds", bounds: [[-7.8, 54.5], [0.0, 60.9]], duration: 1200 },
  },
  {
    name: "energy-trust",
    headline: "But trust in energy companies to act in the public interest has collapsed by 22 points",
    copy: "Energy now occupies a uniquely difficult position in public opinion. Voters increasingly see the sector as vital to the UK economy, but that importance is not translating into goodwill — instead it appears to be heightening scrutiny. As energy costs remain closely tied to household pressure and the cost-of-living debate, voters are more likely to see energy companies as essential but self-interested: necessary to the country, but not trusted to act fairly without oversight.",
    trustStats: [
      { label: "Energy companies · net trust", delta: "−22pts", highlight: true },
      { label: "Oil & gas · net trust", delta: "−28pts", highlight: true, note: "Largest fall of any sector" },
      { label: "35–49s: Q1 net", value: "+3", arrow: "→", value2: "−23", label2: "Q2" },
      { label: "50–64s: Q1 net", value: "−8", arrow: "→", value2: "−30", label2: "Q2" },
    ],
    waveToggleHint: true,
    copy2: "Toggle between Q1 and Q2 in the top-right to see how sharply sentiment shifted in a single quarter — even among the most sympathetic voter groups.",
    insight: "Net trust down −22pts · Oil & gas −28pts",
    mapLabel: "Trust: energy companies · net score · toggle Q1 / Q2",
    mapView: { variable: "trust_energy", compareWaves: false, waveSelect: 4 },
    flyTo: { type: "fitBounds", bounds: [[-7.8, 49.9], [1.6, 58.8]], duration: 1200 },
  },
  {
    name: "back-to-basics",
    headline: "To recover, energy companies — and business in general — need to focus on the basics",
    // bullets rendered by NarrativeCard when step.bullets is set
    bullets: [
      { label: "Competitive pricing & service", delta: "+6pts", value: "45%", dir: "up" },
      { label: "Job creation", delta: "+5pts", value: "40%", dir: "up" },
      { label: "Training & apprenticeships", delta: "+6pts", value: "28%", dir: "up" },
      { label: "Environmental responsibility", delta: "−6pts", value: "24%", dir: "down" },
      { label: "Support for national charities", delta: "−6pts", value: "4%", dir: "down" },
      { label: "Local charity support", delta: "−5pts", value: "12%", dir: "down" },
    ],
    copy: "We ask people what they expect business to prioritise in order to improve trust, and in Q2 the answer is very clearly that they should focus on the basics — help consumers with the cost of living and create jobs and opportunities for young people. London tells this story most vividly. In Q1 it was almost uniformly green — 'treat employees well' dominant across every borough. In Q2 it has fractured into a patchwork of pricing and jobs. Toggle between Q1 and Q2 in the top-right of the map to see the shift.",
    waveToggleHint: true,
    insight: "Pricing +6pts · Environment −6pts",
    mapLabel: "Business priorities · London · toggle Q1 / Q2",
    mapView: { variable: "trust_drivers", compareWaves: false, waveSelect: 4 },
    flyTo: { center: [-0.118, 51.505], zoom: 9.5 },
  },
  {
    name: "segment",
    headline: "Seven segments, seven different routes to engagement",
    copy: "Our MRP model identifies seven distinct attitudinal audiences, each with a different relationship to the issues above. Every constituency contains a mix — the map shows which segment dominates locally.",
    segments: [
      {
        name: "Progressive Idealists",
        pct: "14%",
        color: "#16a34a",
        desc: "Big lefties. Prioritise ethics in purchasing. Very pro-benefits. Most trusting of defence contractors (38% vs 28% nationally).",
      },
      {
        name: "Open Liberals",
        pct: "15%",
        color: "#2563eb",
        desc: "Socially liberal, financially secure, optimistic. Like Starmer, not Corbyn. High trust in businesses. Pro-system, pro-status quo.",
      },
      {
        name: "Hard Bargainers",
        pct: "18%",
        color: "#dc2626",
        desc: "Right-leaning socially, opinionated, commercially discerning. Switched Tory→Labour→Reform. Anti-redistribution but open to windfall taxes.",
      },
      {
        name: "Detached Middle",
        pct: "18%",
        color: "#78716c",
        desc: "Largest weighted group. Lowest political attention, not ideological. Younger, 60% female, least likely to own their home.",
      },
      {
        name: "Disenchanted & Restless",
        pct: "10%",
        color: "#ea580c",
        desc: "Populist-right but pro-redistribution — feel hard done by. Most squeezed financially. Brand loyal, deal-driven consumers.",
      },
      {
        name: "Comfortable Traditionalists",
        pct: "10%",
        color: "#0891b2",
        desc: "Core Tory vote, mostly holding despite Reform pull. Most trusting of business. Most concerned about defence and immigration. Older, wealthy, homeowning.",
      },
      {
        name: "Nostalgic Populists",
        pct: "15%",
        color: "#7c3aed",
        desc: "Reform's base. Most anti-benefit, strongly distrusting — especially of business. Don't feel affluent, believe finances have worsened. Hard line on immigration.",
      },
    ],
    insight: "7 segments · mapped to every constituency",
    mapLabel: "Segmentation · dominant audience by constituency",
    mapView: { variable: "segment", compareWaves: false, waveSelect: 4 },
    flyTo: { type: "fitBounds", bounds: [[-7.8, 49.9], [1.6, 58.8]], duration: 1200 },
  },
  {
    name: "cta",
    headline: "Ready to explore?",
    copy: "",
    insight: "Q3 data lands September",
    isCta: true,
    mapLabel: "Vote intention · Q2 2026",
    mapView: { variable: "vote_intention", compareWaves: false },
    flyTo: { type: "fitBounds", bounds: [[-7.8, 49.9], [1.6, 58.8]] },
  },
];

// ─── DemoSignupOverlay ────────────────────────────────────────────────────────
// Shown on the map panel when the user reaches the final CTA slide.

function DemoSignupOverlay({ embedded = false }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch("/api/user/demo-signup/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Something went wrong");
      // In dev Django returns the verify_url directly; in prod it emails the link.
      setSuccess(data?.verify_url ? `Dev link: ${data.verify_url}` : true);
    } catch (err) {
      setError(err.message || "Unable to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const content = (
      <Box
        sx={{
          width: embedded ? "100%" : "min(420px, 90%)",
          bgcolor: "#fff",
          borderRadius: embedded ? "0" : "16px",
          p: embedded ? 0 : 3.5,
          boxShadow: embedded ? "none" : "0 24px 60px rgba(0,0,0,0.25)",
          border: "none",
        }}
      >
        {success ? (
          <>
            <Typography sx={{ fontSize: "22px", fontWeight: 800, color: "#0f172a", fontFamily: "'DM Sans', sans-serif", mb: 1 }}>
              Check your email
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "#475569", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.6 }}>
              {typeof success === "string"
                ? success
                : "Demo account created. Check your email to verify and activate access."}
            </Typography>
          </>
        ) : (
          <>
            <Typography sx={{ fontSize: "22px", fontWeight: 800, color: "#0f172a", fontFamily: "'DM Sans', sans-serif", mb: 0.5 }}>
              Get demo access
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "#64748b", fontFamily: "'DM Sans', sans-serif", mb: 2.5, lineHeight: 1.6 }}>
              Explore our tracking metrics for Q2 across all 632 constituencies and demographics.
            </Typography>
            <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <TextField
                size="small"
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontFamily: "'DM Sans', sans-serif" } }}
              />
              <TextField
                size="small"
                label="Email"
                type="email"
                required
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(""); }}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontFamily: "'DM Sans', sans-serif" } }}
              />
              <TextField
                size="small"
                label="Password"
                type="password"
                required
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(""); }}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontFamily: "'DM Sans', sans-serif" } }}
              />
              {error && (
                <Typography sx={{ fontSize: "12px", color: "#dc2626", fontFamily: "'DM Sans', sans-serif" }}>
                  {error}
                </Typography>
              )}
              <Button
                type="submit"
                variant="contained"
                disableElevation
                disabled={submitting || !email || !password}
                sx={{
                  bgcolor: "#0d9488", color: "#fff", fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 700, fontSize: "14px", textTransform: "none",
                  borderRadius: "10px", py: 1.1,
                  boxShadow: "0 4px 20px rgba(13,148,136,0.28)",
                  "&:hover": { bgcolor: "#0f766e" },
                  "&:disabled": { bgcolor: "rgba(13,148,136,0.35)", color: "#fff" },
                }}
              >
                {submitting ? "Creating account…" : "Get demo access →"}
              </Button>
            </Box>
          </>
        )}
      </Box>
  );

  if (embedded) {
    return content;
  }

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 20,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "rgba(15,23,42,0.55)",
        backdropFilter: "blur(6px)",
        animation: "fadeInOverlay 0.5s ease both",
        "@keyframes fadeInOverlay": {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
      }}
    >
      {content}
    </Box>
  );
}


// ─── AppPanelWrapper ──────────────────────────────────────────────────────────
// Mounts MainPage directly (not as a route) inside the sticky right panel.
// Uses a no-op navigate so internal URL transitions don't leave /highlights/q2-2026.
// Forwards the ref so Q2HighlightsPage can call applyTheme / setState on the
// MainPage class instance directly from scroll event handlers.

const AppPanelWrapper = React.forwardRef(function AppPanelWrapper(_props, ref) {
  // No-op navigate: prevents any MainPage-internal setThemeInUrl call from
  // routing the browser away from the highlights page.
  const noopNavigate = useCallback(() => {}, []);

  return (
    <MainPage
      ref={ref}
      slug="explore"
      navigate={noopNavigate}
      hideChrome
      skipAuthRedirect
    />
  );
});

// ─── Navigation dispatcher ────────────────────────────────────────────────────
// This is the only place you need to edit when wiring in real app state.
// Each case maps a step name → a direct call on the MainPage class instance.

// ─── Navigation dispatcher ────────────────────────────────────────────────────
// Reads mapView / flyTo config from the STEPS array above.
// You should not need to edit this function — edit the STEPS array instead.

// Holds the pending flyTo2 timeout so we can cancel it on step change
let _flyTo2Timer = null;

function navigateToStep(app, stepName) {
  if (!app) return;
  const step = STEPS.find((s) => s.name === stepName);
  if (!step) return;

  // Cancel any in-flight flyTo2 from a previous step
  if (_flyTo2Timer !== null) {
    clearTimeout(_flyTo2Timer);
    _flyTo2Timer = null;
  }

  // Always clear highlights at the start of each step
  app.setState({ chatHighlightAreas: [] });

  // Apply map variable / wave / comparison state in a single safe call
  if (step.mapView) {
    app.setHighlightsState(step.mapView);
  }

  // Apply zoom/pan
  if (step.flyTo) {
    app.flyTo(step.flyTo);
  }
  if (step.flyTo2) {
    const delay = step.flyTo2Delay ?? 2900;
    _flyTo2Timer = setTimeout(() => {
      _flyTo2Timer = null;
      app.flyTo(step.flyTo2);
      app.setState({ chatHighlightAreas: step.flyTo2Highlights ?? [] });
    }, delay);
  }
}

// ─── Shared style tokens ──────────────────────────────────────────────────────
const F = "'Inter', 'Roboto', sans-serif";
const TEAL = "#0d9488";
const INK = "#0f172a";
const MUTED = "#475569";
const MUTED_LIGHT = "#94a3b8";
const PAPER = "#ffffff";
const PAPER_SOFT = "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)";
const BORDER = "rgba(15,23,42,0.08)";

// ─── Sub-components ───────────────────────────────────────────────────────────

function StepHeader({ index, total, insight, isActive }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: "auto", pt: 3.5, opacity: isActive ? 1 : 0, transition: "opacity 0.35s ease", minHeight: 28, flexWrap: "wrap" }}>
      <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: TEAL, fontFamily: F }}>
        {index + 1} of {total}
      </Typography>
      <Chip label={insight} size="small" sx={{ height: 24, fontSize: "10px", fontWeight: 700, letterSpacing: "0.02em", fontFamily: F, bgcolor: "rgba(13,148,136,0.08)", color: TEAL, border: "1px solid rgba(13,148,136,0.16)", "& .MuiChip-label": { px: 1.1 } }} />
    </Box>
  );
}

function Eyebrow({ children }) {
  return (
    <Typography sx={{ fontSize: "10px", fontWeight: 800, letterSpacing: "0.16em", textTransform: "uppercase", color: TEAL, fontFamily: F, mb: 1.6 }}>
      {children}
    </Typography>
  );
}

function Headline({ children }) {
  return (
    <Typography sx={{ fontSize: { xs: "26px", md: "34px" }, fontWeight: 800, lineHeight: { xs: 1.18, md: 1.12 }, letterSpacing: "-0.04em", color: INK, fontFamily: F, mb: 2.25, maxWidth: 620 }}>
      {children}
    </Typography>
  );
}

function BodyCopy({ children }) {
  return (
    <Typography sx={{ fontSize: { xs: "14px", md: "15px" }, color: MUTED, lineHeight: 1.82, fontFamily: F, maxWidth: 560, whiteSpace: "pre-line", letterSpacing: "-0.01em" }}>
      {children}
    </Typography>
  );
}

function SectionLabel({ children }) {
  return (
    <Typography sx={{ fontSize: "10px", fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: MUTED_LIGHT, fontFamily: F, mb: 1.1 }}>
      {children}
    </Typography>
  );
}

function Panel({ children, sx = {} }) {
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 590,
        p: { xs: 2, md: 2.5 },
        borderRadius: "22px",
        bgcolor: PAPER,
        backgroundImage: PAPER_SOFT,
        border: `1px solid ${BORDER}`,
        boxShadow: "0 18px 38px rgba(15,23,42,0.06)",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function WaveHint() {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.9, mt: 2.25, px: 1.4, py: 0.9, borderRadius: "999px", bgcolor: "rgba(13,148,136,0.06)", border: "1px solid rgba(13,148,136,0.15)", width: "fit-content" }}>
      <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: TEAL, flexShrink: 0 }} />
      <Typography sx={{ fontSize: "11px", color: TEAL, fontFamily: F, fontWeight: 700, letterSpacing: "0.01em" }}>
        Toggle Q1 / Q2 in the top-right of the map
      </Typography>
    </Box>
  );
}

// Card 1 — methodology layout
function IntroCard({ isActive, step }) {
  return (
    <>
      <Eyebrow>Quarterly Briefing</Eyebrow>
      <Headline>{step.headline}</Headline>

      <Panel sx={{ mb: 2.75 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.5, mb: 2.5 }}>
          {[["10,232", "adults surveyed"], ["632", "constituencies modelled"], ["Q2 2026", "latest wave"]].map(([val, lbl]) => (
            <Box key={lbl} sx={{ borderLeft: "2px solid rgba(13,148,136,0.18)", pl: 1.4 }}>
              <Typography sx={{ fontSize: "24px", fontWeight: 800, color: INK, fontFamily: F, lineHeight: 1 }}>{val}</Typography>
              <Typography sx={{ fontSize: "11px", color: MUTED_LIGHT, fontFamily: F, mt: 0.5, lineHeight: 1.35, letterSpacing: "0.02em" }}>{lbl}</Typography>
            </Box>
          ))}
        </Box>

        <SectionLabel>Methodology</SectionLabel>
        <BodyCopy>
          Stack surveyed 10,232 British adults between 24 March and 20 April 2026, online. The sample was balanced to be regionally-representative by age, gender, ethnicity, education, past vote, region, and socioeconomic class.
        </BodyCopy>
        <Box sx={{ mt: 1.6 }}>
          <BodyCopy>
            Constituency Profiles uses <strong style={{ color: INK, fontWeight: 700 }}>MRP modelling</strong> — multilevel regression establishes the relationship between a respondent's detailed demography and their survey answers; poststratification then projects those estimates onto the known demographic composition of each area.
          </BodyCopy>
        </Box>
      </Panel>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, opacity: isActive ? 1 : 0, transition: "opacity 0.5s ease 0.3s" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "3px" }}>
          {[0, 0.15, 0.3].map((d) => (
            <Box key={d} sx={{ width: 16, height: 2, borderRadius: 1, bgcolor: TEAL, opacity: 0.5, animation: `scrollCue 1.4s ease-in-out ${d}s infinite`, "@keyframes scrollCue": { "0%,100%": { transform: "scaleX(1)", opacity: 0.4 }, "50%": { transform: "scaleX(0.6)", opacity: 0.9 } } }} />
          ))}
        </Box>
        <Typography sx={{ fontSize: "11px", color: MUTED_LIGHT, fontFamily: F, letterSpacing: "0.06em", textTransform: "uppercase" }}>
          Scroll to explore
        </Typography>
      </Box>
    </>
  );
}

// Card 2 — bullets layout
function BackToBasicsCard({ step }) {
  return (
    <>
      <Eyebrow>Trust Repair</Eyebrow>
      <Headline>{step.headline}</Headline>
      <Panel sx={{ mb: 2.2 }}>
        <SectionLabel>What Voters Want Business To Prioritise</SectionLabel>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.85 }}>
          {step.bullets.map((b) => (
            <Box key={b.label} sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1.25, py: 1, borderRadius: "12px", bgcolor: b.dir === "up" ? "rgba(16,185,129,0.05)" : "rgba(239,68,68,0.05)", border: `1px solid ${b.dir === "up" ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.12)"}` }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 800, fontFamily: F, color: b.dir === "up" ? "#059669" : "#dc2626", minWidth: 50 }}>
                {b.delta}
              </Typography>
              <Typography sx={{ fontSize: "13px", color: INK, fontFamily: F, flex: 1, lineHeight: 1.35 }}>{b.label}</Typography>
              <Typography sx={{ fontSize: "12px", color: MUTED_LIGHT, fontFamily: F, fontWeight: 700 }}>{b.value}</Typography>
            </Box>
          ))}
        </Box>
      </Panel>
      <BodyCopy>{step.copy}</BodyCopy>
      {step.waveToggleHint && <WaveHint />}
    </>
  );
}

// Card 3 — sector falls + Scotland list
function EnergyImportanceCard({ step }) {
  return (
    <>
      <Eyebrow>{step.name === "scotland-energy" ? "Geographic Signal" : "Sector Salience"}</Eyebrow>
      <Headline>{step.headline}</Headline>
      <BodyCopy>{step.copy}</BodyCopy>

      {step.sectorFalls && (
        <Panel sx={{ mt: 2.2, mb: 2.6 }}>
          <SectionLabel>Sectors That Fell Back</SectionLabel>
          {step.sectorFalls.map((s) => (
            <Box key={s.label} sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", mb: 1 }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 800, color: "#dc2626", fontFamily: F, minWidth: 52 }}>{s.delta}</Typography>
              <Box>
                <Typography sx={{ fontSize: "13px", color: INK, fontFamily: F, lineHeight: 1.45 }}>{s.label}{s.from ? ` (${s.from} → ${s.to})` : s.to ? ` → ${s.to}` : ""}</Typography>
                {s.note ? <Typography sx={{ fontSize: "11px", color: MUTED_LIGHT, fontFamily: F, lineHeight: 1.4, mt: 0.15 }}>{s.note}</Typography> : null}
              </Box>
            </Box>
          ))}
        </Panel>
      )}

      {step.mapHoverHint && (
        <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: "11px", color: TEAL, fontFamily: F, opacity: 0.85 }}>
            ↗
          </Typography>
          <Typography sx={{ fontSize: "12px", color: MUTED_LIGHT, fontFamily: F, fontStyle: "italic" }}>
            Hover over the map to explore sector importance by constituency
          </Typography>
        </Box>
      )}

      {step.scotlandConstituencies && (
      <Panel sx={{ mt: 2.5 }}>
        <SectionLabel>Scottish Constituencies With The Biggest Gains</SectionLabel>
        {step.scotlandConstituencies.map((c, i) => (
          <Box key={c.name} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
            <Typography sx={{ fontSize: "11px", color: MUTED_LIGHT, fontFamily: F, minWidth: 14 }}>{i + 1}.</Typography>
            <Typography sx={{ fontSize: "13px", color: INK, fontFamily: F, flex: 1 }}>{c.name}</Typography>
            <Typography sx={{ fontSize: "12px", fontWeight: 800, color: "#059669", fontFamily: F }}>{c.delta}</Typography>
            <Typography sx={{ fontSize: "11px", color: MUTED_LIGHT, fontFamily: F }}>({c.now})</Typography>
          </Box>
        ))}
        <Typography sx={{ fontSize: "13px", color: MUTED, fontFamily: F, mt: 1.4, lineHeight: 1.72, maxWidth: 520 }}>{step.scotlandNote}</Typography>
      </Panel>
      )}
    </>
  );
}

// Card 5 — trust stats
function EnergyTrustCard({ step }) {
  return (
    <>
      <Eyebrow>Trust Collapse</Eyebrow>
      <Headline>{step.headline}</Headline>
      <BodyCopy>{step.copy}</BodyCopy>

      <Panel sx={{ mt: 2.2, mb: 2.2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.85 }}>
          {step.trustStats.map((s) => (
            <Box key={s.label} sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1.25, py: 1, borderRadius: "12px", bgcolor: "rgba(239,68,68,0.05)", border: "1px solid rgba(239,68,68,0.12)" }}>
              {s.highlight ? (
                <>
                  <Typography sx={{ fontSize: "15px", fontWeight: 800, color: "#dc2626", fontFamily: F, minWidth: 52 }}>{s.delta}</Typography>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: "13px", color: INK, fontFamily: F, lineHeight: 1.35 }}>{s.label}</Typography>
                    {s.note && <Typography sx={{ fontSize: "11px", color: MUTED_LIGHT, fontFamily: F, mt: 0.15 }}>{s.note}</Typography>}
                  </Box>
                </>
              ) : (
                <>
                  <Typography sx={{ fontSize: "13px", color: MUTED, fontFamily: F, flex: 1 }}>{s.label}</Typography>
                  <Typography sx={{ fontSize: "13px", fontWeight: 700, color: s.value?.startsWith("+") ? "#059669" : "#dc2626", fontFamily: F }}>{s.value}</Typography>
                  <Typography sx={{ fontSize: "11px", color: MUTED_LIGHT, fontFamily: F, mx: 0.5 }}>{s.arrow}</Typography>
                  <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#dc2626", fontFamily: F }}>{s.value2}</Typography>
                  <Typography sx={{ fontSize: "11px", color: MUTED_LIGHT, fontFamily: F }}>{s.label2}</Typography>
                </>
              )}
            </Box>
          ))}
        </Box>
      </Panel>

      <BodyCopy>{step.copy2}</BodyCopy>
      {step.waveToggleHint && <WaveHint />}
    </>
  );
}

// Card 5 — defence demographic stats
// Card 6 — segment cards
function SegmentCard({ step }) {
  return (
    <>
      <Eyebrow>Audience Map</Eyebrow>
      <Headline>{step.headline}</Headline>
      <BodyCopy>{step.copy}</BodyCopy>

      <Panel sx={{ mt: 2.2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {step.segments.map((s) => (
            <Box key={s.name} sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, px: 1.3, py: 1, borderRadius: "14px", bgcolor: "#f8f9fc", border: "1px solid rgba(15,23,42,0.07)", borderLeft: `3px solid ${s.color}` }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.3 }}>
                  <Typography sx={{ fontSize: "13px", fontWeight: 700, color: INK, fontFamily: F }}>{s.name}</Typography>
                  <Typography sx={{ fontSize: "11px", color: MUTED_LIGHT, fontFamily: F, fontWeight: 700 }}>· {s.pct}</Typography>
                </Box>
                <Typography sx={{ fontSize: "12px", color: MUTED, fontFamily: F, lineHeight: 1.5 }}>{s.desc}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Panel>
    </>
  );
}

// ─── NarrativeCard — routes to the right sub-component ───────────────────────

function NarrativeCard({ step, index, total, isActive, cardRef, authState }) {
  const navigate = useNavigate();

  const cardSx = {
    // mobile: height:auto so cards size to content (no internal scroll box)
    // minHeight:60vh gives the IntersectionObserver enough scroll distance per step
    // desktop: fixed viewport height keeps snap-scroll working
    height: { xs: "auto", md: "calc(100vh - 47px)" },
    minHeight: { xs: "60vh", md: "unset" },
    flexShrink: 0,
    scrollSnapAlign: { xs: "none", md: "start" },
    scrollSnapStop: { xs: "normal", md: "always" },
    display: "flex",
    flexDirection: "column",
    justifyContent: step.isCta ? "center" : "flex-start",
    alignItems: step.isCta ? "center" : "flex-start",
    pt: { xs: 5, md: "15vh" },
    pb: { xs: 5, md: 7 },
    px: { xs: 3, md: 4.5 },
    position: "relative",
    overflowY: { xs: "visible", md: "auto" }, // mobile: no internal scroll; window is the scroll container
    borderLeft: "1px solid",
    borderColor: isActive ? "rgba(13,148,136,0.2)" : "transparent",
    opacity: isActive ? 1 : 0.4,
    transition: "opacity 0.4s ease, border-color 0.4s ease",
    background: isActive ? "linear-gradient(90deg, rgba(13,148,136,0.04) 0%, rgba(13,148,136,0) 18%)" : "transparent",
  };

  if (step.isCta) {
    return (
      <Box ref={cardRef} data-index={index} sx={cardSx}>
        {isActive ? (
          <Box
            sx={{
              position: { xs: "relative", md: "fixed" },
              inset: { xs: "auto", md: "47px 0 0 0" },
              zIndex: 20,
              height: { xs: "auto", md: "calc(100vh - 47px)" }, /* auto on mobile: card is height:auto so 100% would collapse */
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              px: { xs: 0, md: 6 },
            }}
          >
            <Panel sx={{ maxWidth: 560, textAlign: "center", p: { xs: 3, md: 4 }, pointerEvents: "auto" }}>
              <Eyebrow>Next Step</Eyebrow>
              <Typography sx={{ fontSize: { xs: "30px", md: "38px" }, fontWeight: 800, letterSpacing: "-0.04em", color: INK, fontFamily: F, mb: 1.2, textAlign: "center" }}>
                {step.headline}
              </Typography>

              {authState === "loggedIn" && (
                <>
                  <Typography sx={{ fontSize: "15px", color: MUTED, fontFamily: F, mb: 3.2, textAlign: "center", lineHeight: 1.8 }}>
                    Everything above is live in the dashboard, filterable by constituency, demographic, and segment. Jump straight in.
                  </Typography>
                  <Button variant="contained" disableElevation onClick={() => navigate(shouldGoMobile() ? "/m?section=map" : "/explore")}
                    sx={{ bgcolor: TEAL, color: "#fff", fontFamily: F, fontWeight: 700, fontSize: "14px", textTransform: "none", borderRadius: "999px", px: 3.5, py: 1.25, boxShadow: "0 12px 26px rgba(13,148,136,0.22)", "&:hover": { bgcolor: "#0f766e" } }}>
                    Open the dashboard
                  </Button>
                </>
              )}

              {authState === "loggedOut" && (
                <>
                  <Typography sx={{ fontSize: "15px", color: MUTED, fontFamily: F, mb: 3.2, textAlign: "center", lineHeight: 1.8 }}>
                    Everything above is live in the dashboard, filterable by constituency, demographic, and segment. Log in to access it.
                  </Typography>
                  <Button variant="contained" disableElevation onClick={() => navigate("/")}
                    sx={{ bgcolor: TEAL, color: "#fff", fontFamily: F, fontWeight: 700, fontSize: "14px", textTransform: "none", borderRadius: "999px", px: 3.5, py: 1.25, boxShadow: "0 12px 26px rgba(13,148,136,0.22)", "&:hover": { bgcolor: "#0f766e" } }}>
                    Log in
                  </Button>
                </>
              )}

              {authState === "guest" && (
                <>
                  <Typography sx={{ fontSize: "15px", color: MUTED, fontFamily: F, mb: 2.5, textAlign: "center", lineHeight: 1.8 }}>
                    Everything above is live in the dashboard, filterable by constituency, demographic, and segment. Leave your details and we'll set you up with full access.
                  </Typography>
                  <DemoSignupOverlay embedded />
                </>
              )}
            </Panel>
          </Box>
        ) : null}
      </Box>
    );
  }

  const inner = (() => {
    switch (step.name) {
      case "intro":           return <IntroCard isActive={isActive} step={step} />;
      case "back-to-basics":  return <BackToBasicsCard step={step} />;
      case "energy-importance": return <EnergyImportanceCard step={step} />;
      case "scotland-energy":   return <EnergyImportanceCard step={step} />;
      case "energy-trust":    return <EnergyTrustCard step={step} />;
      case "segment":         return <SegmentCard step={step} />;
      default:
        return (
          <>
            <Headline>{step.headline}</Headline>
            {step.copy && <BodyCopy>{step.copy}</BodyCopy>}
          </>
        );
    }
  })();

  return (
    <Box ref={cardRef} data-index={index} sx={cardSx}>
      {inner}
      <StepHeader index={index} total={total} insight={step.insight} isActive={isActive} />
    </Box>
  );
}

// ─── Main page component ──────────────────────────────────────────────────────

export default function Q2HighlightsPage() {
  const { status: authStatus, authState } = useHighlightsAuth();
  const navigate = useNavigate();

  const [activeIndex, setActiveIndex] = useState(0);
  const [waveOverride, setWaveOverride] = useState(null); // null = use step default
  const [mapFullscreen, setMapFullscreen] = useState(false); // xs only: fullscreen overlay toggle
  const cardRefs = useRef([]);        // array of refs, one per narrative card
  const visibleSet = useRef(new Set()); // tracks which card indices are in the active zone
  const mainRef = useRef(null);       // ref to the MainPage class instance
  const scrollContainerRef = useRef(null); // the snap scroll container
  const appPanelRef        = useRef(null); // ref for imperative wheel/touch listeners
  const activeIndexRef     = useRef(activeIndex); // always-current index for touch handler closure
  const isFinalStep = STEPS[activeIndex]?.name === "cta";

  // Keep activeIndexRef in sync so touch-end closure always reads the latest step
  useEffect(() => { activeIndexRef.current = activeIndex; }, [activeIndex]);

  // Tell Mapbox to redraw at the new container size after a fullscreen toggle.
  // Mapbox GL does not auto-detect CSS size changes — resize() must be called explicitly.
  // Called immediately (starts tracking the animation) and again at 350ms (post-transition,
  // after the 0.3s ease completes) so the canvas ends up exactly filling the final container.
  useEffect(() => {
    // Ref chain: MainPage → MapPage (class) → react-map-gl MapRef → getMap() → Mapbox GL map.
    // mapRef.current is MapPage (a class component), not the react-map-gl MapRef directly,
    // so one extra .mapRef.current hop is needed vs the naive assumption.
    const getMapInstance = () =>
      mainRef.current?.mapRef?.current?.mapRef?.current?.getMap?.();

    // Immediate resize: kick Mapbox out of its stale-canvas state as the CSS animates.
    getMapInstance()?.resize?.();

    // Post-transition: final resize then re-apply the current step's intended camera.
    //
    // Why re-apply the step camera rather than preserving captured bounds:
    //   getBounds() is viewport-shape-dependent. Round-tripping between a tall fullscreen
    //   and a short panel compounds the error each cycle (wider bounds → extreme zoom-out).
    //   The step's flyTo config is the canonical, viewport-independent source of truth.
    //   Re-applying it fills the new canvas correctly for every step type:
    //     UK-wide step   → fitBounds(UK_BOUNDS) on the tall canvas → UK fills fullscreen
    //     Zoomed step    → fitBounds(step.bounds) on the tall canvas → area fills fullscreen
    //     Either step    → collapse does the same re-frame to the short panel
    //   Stable across any number of toggles: always reads fixed config, never live bounds.
    //
    // activeIndexRef (not activeIndex) avoids stale closure — kept in sync via useEffect.
    const t = setTimeout(() => {
      const m = getMapInstance();
      if (!m) return;
      m.resize();
      const step = STEPS[activeIndexRef.current];
      if (step?.flyTo) {
        mainRef.current?.flyTo?.(step.flyTo);
      }
    }, 350);

    return () => clearTimeout(t);
  }, [mapFullscreen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWaveToggle = (wave) => {
    if (!mainRef.current) return;
    setWaveOverride(wave);
    const step = STEPS[activeIndex];
    if (step?.mapView) {
      mainRef.current.setHighlightsState({ ...step.mapView, waveSelect: wave });
    }
  };

  // ── Dispatch app navigation when active step changes ────────────────────────
  useEffect(() => {
    if (authStatus !== "ready") return;
    setWaveOverride(null); // reset manual toggle on every step change
    const stepName = STEPS[activeIndex]?.name;
    if (stepName) navigateToStep(mainRef.current, stepName);
  }, [activeIndex, authStatus]);

  // ── IntersectionObserver: vanilla JS, no library ─────────────────────────
  // A card becomes "active" when its top edge crosses 40% down from the top
  // of the viewport. We track all currently-visible cards and make the
  // highest-index one active (most recently scrolled into view).
  useEffect(() => {
    if (authStatus !== "ready") return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const idx = Number(entry.target.dataset.index);
          if (entry.isIntersecting) {
            visibleSet.current.add(idx);
          } else {
            visibleSet.current.delete(idx);
          }
        });

        if (visibleSet.current.size > 0) {
          // Highest index = last card to enter the active zone while scrolling down.
          // Falls back to lowest when scrolling back up (last to exit the zone).
          setActiveIndex(Math.max(...visibleSet.current));
        }
      },
      {
        // Desktop: observe within the snap-scroll container.
        // Mobile: container is height:auto and window is the scroll element,
        // so use null (viewport) as the root so the observer fires on window scroll.
        root: window.innerWidth < 900 ? null : scrollContainerRef.current,
        rootMargin: "0px 0px -40% 0px",
        threshold: 0,
      }
    );

    const cards = cardRefs.current;
    cards.forEach((card) => { if (card) observer.observe(card); });

    return () => observer.disconnect();
  }, [authStatus]);

  // ── Wheel (desktop) + touch (mobile) — non-passive, imperative ───────────
  // Fixes three issues found in investigation:
  //   1. React's onWheel is passive → preventDefault is a no-op → replaced with
  //      addEventListener('wheel', ..., { passive: false })
  //   2. Mobile container is height:auto (window scrolls, not container) →
  //      touch handler uses scrollIntoView on the target card ref
  //   3. No touch handler existed — added touchstart/touchmove/touchend
  useEffect(() => {
    if (authStatus !== "ready") return;
    const panel     = appPanelRef.current;
    const container = scrollContainerRef.current;
    if (!panel) return;

    const isMobile = () => window.innerWidth < 900;

    // ── Wheel (desktop: scroll container; mobile: scroll to card via index) ──
    const onWheel = (e) => {
      e.preventDefault();
      if (isMobile()) {
        // Mobile has no snap container — scroll to the adjacent card
        const current = activeIndexRef.current;
        const next = e.deltaY > 0
          ? Math.min(current + 1, STEPS.length - 1)
          : Math.max(current - 1, 0);
        if (next === current) return;
        setActiveIndex(next);
        requestAnimationFrame(() => {
          cardRefs.current[next]?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      } else {
        // Desktop: drive snap-scroll container
        if (!container || container._scrolling) return;
        container._scrolling = true;
        setTimeout(() => { container._scrolling = false; }, 800);
        container.scrollBy({
          top: e.deltaY > 0 ? container.clientHeight : -container.clientHeight,
          behavior: "smooth",
        });
      }
    };

    // ── Touch (mobile primarily): one step per gesture ────────────────────
    let touchStartY = 0;

    const onTouchStart = (e) => {
      touchStartY = e.touches[0].clientY;
    };

    // Prevent page scroll while the finger is on the map panel
    const onTouchMove = (e) => {
      e.preventDefault();
    };

    const onTouchEnd = (e) => {
      const delta = touchStartY - e.changedTouches[0].clientY;
      if (Math.abs(delta) < 30) return; // ignore taps / micro-swipes
      const current = activeIndexRef.current;
      const next = delta > 0
        ? Math.min(current + 1, STEPS.length - 1)
        : Math.max(current - 1, 0);
      if (next === current) return;
      setActiveIndex(next);
      requestAnimationFrame(() => {
        cardRefs.current[next]?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    };

    panel.addEventListener("wheel",      onWheel,      { passive: false });
    panel.addEventListener("touchstart", onTouchStart, { passive: true  });
    panel.addEventListener("touchmove",  onTouchMove,  { passive: false });
    panel.addEventListener("touchend",   onTouchEnd,   { passive: true  });

    return () => {
      panel.removeEventListener("wheel",      onWheel);
      panel.removeEventListener("touchstart", onTouchStart);
      panel.removeEventListener("touchmove",  onTouchMove);
      panel.removeEventListener("touchend",   onTouchEnd);
    };
  }, [authStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Progress: 0–100 based on active step index ────────────────────────────
  const progress = ((activeIndex) / (STEPS.length - 1)) * 100;

  // ── Loading state ─────────────────────────────────────────────────────────
  if (authStatus === "loading") {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          bgcolor: "#f8f9fc",
        }}
      >
        <CircularProgress size={32} sx={{ color: "#0d9488" }} />
      </Box>
    );
  }

  return (
    /*
     * Layout strategy — desktop vs mobile:
     *
     * Desktop (md+):
     *   The app panel is `position: fixed` — pinned to the right 55% of the
     *   viewport at all times. `position: sticky` inside flex has too many
     *   browser-specific pitfalls (flex stretch, overflow ancestors, etc.).
     *   Fixed is unambiguous: it's always in the viewport.
     *   The narrative column gets `marginRight: "55%"` to live in the left 45%.
     *
     * Mobile (xs):
     *   Panel is `position: relative`, full-width, 58 vh tall — sits at the
     *   top of the page. Cards scroll below it in normal document flow.
     */
    <Box sx={{ bgcolor: "#f8f9fc", height: { xs: "auto", md: "100vh" }, overflow: { xs: "visible", md: "hidden" } }}>

      {/* ── Progress bar ───────────────────────────────────────────────────── */}
      <Box
        role="progressbar"
        aria-valuenow={Math.round(progress)}
        sx={{
          position: "fixed",
          top: 0, left: 0,
          width: "100%", height: "3px",
          // Opaque equivalent of rgba(13,148,136,0.15) over the #f8f9fc page bg —
          // same look, but stops page text bleeding through the track while scrolling.
          bgcolor: "#d5eaeb",
          zIndex: 1200,
        }}
      >
        <Box sx={{ height: "100%", width: `${progress}%`, bgcolor: "#0d9488", transition: "width 0.4s ease" }} />
      </Box>

      {/* ── Branding strip ─────────────────────────────────────────────────── */}
      <Box
        sx={{
          position: "fixed",
          top: 3, left: 0, right: 0,
          height: 44,
          display: "flex", alignItems: "center",
          px: 3,
          zIndex: 1100,
          bgcolor: "rgba(248,249,252,0.92)",
          backdropFilter: "blur(8px)",
          borderBottom: "0.5px solid rgba(15,23,42,0.07)",
        }}
      >
        <Typography sx={{ fontSize: "13px", fontWeight: 700, fontFamily: "'DM Sans', sans-serif", color: "#0f172a", letterSpacing: "-0.01em" }}>
          Constituency Profiles
        </Typography>
        <Box sx={{ ml: 1.5, px: 1, py: 0.25, borderRadius: "5px", bgcolor: "rgba(13,148,136,0.09)", border: "1px solid rgba(13,148,136,0.2)" }}>
          <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#0d9488", fontFamily: "'DM Sans', sans-serif" }}>
            Q2 2026 Highlights
          </Typography>
        </Box>
        {/* X close button — always visible, returns to main app */}
        <Box
          onClick={() => navigate(shouldGoMobile() ? "/m?section=map" : "/explore")}
          sx={{
            ml: "auto",
            width: 28, height: 28,
            display: "flex", alignItems: "center", justifyContent: "center",
            borderRadius: "6px",
            cursor: "pointer",
            color: "rgba(15,23,42,0.4)",
            "&:hover": { bgcolor: "rgba(15,23,42,0.06)", color: "rgba(15,23,42,0.7)" },
            transition: "all 0.15s ease",
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </Box>
      </Box>

      {/* ── App panel ──────────────────────────────────────────────────────── */}
      {/* Desktop: fixed to right 55% of viewport, below the 47px branding strip */}
      {/* Mobile:  relative, full width, 58 vh — sits above the narrative cards  */}
      {/* Wheel + touch listeners are registered imperatively (passive:false) in  */}
      {/* the useEffect above — React's onWheel is passive and preventDefault fails */}
      <Box
        ref={appPanelRef}
        sx={{
          /* fixed on mobile too — immune to body{overflow:auto} which breaks sticky.
             left:0 on mobile makes it full-width; desktop keeps right:0 + 55% width.
             mapFullscreen (xs only) expands to cover the full viewport for inspection. */
          position: "fixed",
          top: { xs: mapFullscreen ? 0 : "47px", md: "47px" },
          left: { xs: 0, md: "auto" },
          right: 0,
          width: { xs: "100%", md: isFinalStep ? "100%" : "55%" },
          height: { xs: mapFullscreen ? "100dvh" : "30dvh", md: "calc(100vh - 47px)" },
          zIndex: { xs: mapFullscreen ? 1300 : 5, md: 5 },
          overflow: "hidden",
          borderLeft: { md: isFinalStep ? "none" : "0.5px solid rgba(15,23,42,0.07)" },
          bgcolor: "background.paper",
          transition: "width 0.45s ease, height 0.3s ease, top 0.3s ease",
        }}
      >
        <Box sx={{ height: "100%", position: "relative", overflow: "hidden" }}>
          <Box sx={{ height: "100%", filter: isFinalStep ? "blur(10px) saturate(0.85)" : "none", transform: isFinalStep ? "scale(1.03)" : "none", transition: "filter 0.4s ease, transform 0.4s ease" }}>
          <AppPanelWrapper ref={mainRef} />
          </Box>

          {isFinalStep && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                bgcolor: "rgba(248,249,252,0.3)",
                backgroundImage: "linear-gradient(180deg, rgba(248,249,252,0.12) 0%, rgba(248,249,252,0.5) 100%)",
                pointerEvents: "none",
                zIndex: 2,
              }}
            />
          )}

          {/* Map label — floats top-left, shows what variable is being displayed */}
          <Box
            sx={{
              position: "absolute",
              top: 12, left: 12,
              zIndex: 10,
              pointerEvents: "none",
              opacity: isFinalStep ? 0 : 1,
              transition: "opacity 0.25s ease",
            }}
          >
            <Box
              key={activeIndex}
              sx={{
                display: "inline-flex", alignItems: "center", gap: 0.75,
                px: 1.5, py: 0.65,
                borderRadius: "8px",
                bgcolor: "rgba(255,255,255,0.93)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(13,148,136,0.18)",
                boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
              }}
            >
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#0d9488", flexShrink: 0 }} />
              <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#0f172a", fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.01em", lineHeight: 1 }}>
                {STEPS[activeIndex]?.mapLabel}
              </Typography>
            </Box>
          </Box>

          {/* Wave toggle — step 2 (back-to-basics) and step 4 (energy-trust) */}
          {(STEPS[activeIndex]?.name === "back-to-basics" || STEPS[activeIndex]?.name === "energy-trust") && (
            <Box
              sx={{
                position: "absolute",
                top: 12, right: 12,
                zIndex: 10,
                display: "flex",
                borderRadius: "10px",
                overflow: "hidden",
                border: "1px solid rgba(13,148,136,0.25)",
                boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
              }}
            >
              {[3, 4].map((w) => {
                const active = (waveOverride ?? 4) === w;
                return (
                  <Box
                    key={w}
                    onClick={() => handleWaveToggle(w)}
                    sx={{
                      px: 1.75, py: 0.75,
                      cursor: "pointer",
                      bgcolor: active ? "#0d9488" : "rgba(255,255,255,0.93)",
                      backdropFilter: "blur(10px)",
                      color: active ? "#fff" : "#475569",
                      fontSize: "11px",
                      fontWeight: 700,
                      fontFamily: "'DM Sans', sans-serif",
                      letterSpacing: "0.03em",
                      transition: "all 0.18s ease",
                      userSelect: "none",
                      "&:hover": { bgcolor: active ? "#0f766e" : "rgba(13,148,136,0.08)" },
                    }}
                  >
                    {w === 3 ? "Q1 2026" : "Q2 2026"}
                  </Box>
                );
              })}
            </Box>
          )}

          {/* ── Map fullscreen toggle — xs only ─────────────────────────────
               Tap to expand the small map to a full-screen overlay for inspection.
               Tap again (or the X) to collapse back. Scroll position and current
               step are fully preserved — the overlay is purely CSS, the narrative
               scroll container is untouched underneath.                          */}
          <Box
            onClick={() => setMapFullscreen(f => !f)}
            sx={{
              display: { xs: "flex", md: "none" },
              position: "absolute",
              bottom: 12, right: 12,
              zIndex: 20,
              width: 36, height: 36,
              borderRadius: "10px",
              bgcolor: "rgba(255,255,255,0.92)",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(13,148,136,0.2)",
              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
              alignItems: "center", justifyContent: "center",
              cursor: "pointer",
              userSelect: "none",
              WebkitTapHighlightColor: "transparent",
              transition: "background 0.15s ease",
              "&:active": { bgcolor: "rgba(13,148,136,0.12)" },
            }}
          >
            {mapFullscreen ? (
              /* Collapse: X */
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            ) : (
              /* Expand: outward diagonal arrows */
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/>
                <line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>
              </svg>
            )}
          </Box>

          {/* Step indicator — floats above the app UI */}
          <Box
            sx={{
              position: "absolute",
              bottom: 16, left: 16,
              zIndex: 10,
              pointerEvents: "none",
              opacity: isFinalStep ? 0 : 1,
              transition: "opacity 0.25s ease",
            }}
          >
            <Box
              sx={{
                display: "flex", alignItems: "center", gap: 1,
                px: 1.5, py: 0.75,
                borderRadius: "10px",
                bgcolor: "rgba(255,255,255,0.92)",
                backdropFilter: "blur(8px)",
                border: "1px solid rgba(13,148,136,0.2)",
                boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
              }}
            >
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#0d9488", flexShrink: 0 }} />
              <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#0d9488", fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.04em", textTransform: "uppercase", lineHeight: 1 }}>
                {activeIndex + 1} / {STEPS.length}
              </Typography>
              <Typography sx={{ fontSize: "11px", fontWeight: 500, color: "#475569", fontFamily: "'DM Sans', sans-serif", lineHeight: 1, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {STEPS[activeIndex]?.headline}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Narrative cards column — snap scroll container ─────────────────── */}
      <Box
        ref={scrollContainerRef}
        sx={{
          mr: { xs: 0, md: isFinalStep ? 0 : "55%" },
          /* mobile: push cards below fixed panel (50vh) + branding strip (47px) */
          pt: { xs: "calc(30dvh + 47px)", md: "47px" },
          height: { xs: "auto", md: "100vh" },
          overflowY: { xs: "visible", md: "scroll" }, /* mobile: not a scroll container; window scrolls instead */
          scrollSnapType: { xs: "none", md: "y mandatory" },
          // Hide scrollbar visually but keep it functional
          "&::-webkit-scrollbar": { display: "none" },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        {STEPS.map((step, i) => (
          <NarrativeCard
            key={step.name}
            step={step}
            index={i}
            total={STEPS.length}
            isActive={activeIndex === i}
            authState={authState}
            cardRef={(el) => { cardRefs.current[i] = el; }}
          />
        ))}
      </Box>
    </Box>
  );
}
